import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import "react-responsive-carousel/lib/styles/carousel.min.css";
import Lightbox from "yet-another-react-lightbox";
import "yet-another-react-lightbox/styles.css";
import config from "../../config/config.json";
import useAlertModal from "../../hooks/useAlertModal";
import { useAppAuthState } from "../../hooks/useAppAuthState";
import { useUser } from "../../hooks/useUser";
import { createPlaceFromGooglePlaceResultIfDNE } from "../../lib/create-place-from-google-place-result";
import { summarizeTableSituation } from "../../lib/summarize-table-situation";
import { vibeTypeToText } from "../../lib/vibe-type-to-text";
import { Place } from "../../types/Place";
import { clearMarkerHighlight, highlightMarker } from "./highlight-marker";
import { removeMarkerFromMap } from "./remove-marker-from-map";
import { renderGooglePlacesMarker } from "./render-google-places-marker";
import UserLocationMarker from "./UserLocationMarker";

interface MapComponentProps {
  center: google.maps.LatLngLiteral;
  userLocation: google.maps.LatLngLiteral | null;
  zoom: number;
  highlightedPlaceId?: string | null;
  googlePlaceResult?: google.maps.places.PlaceResult | null;
  onClearGooglePlaceResult: () => void;
  places: Place[];
  onMapLoad: (map: google.maps.Map) => void;
}

const MapComponent: React.FC<MapComponentProps> = ({
  center,
  zoom,
  userLocation,
  highlightedPlaceId: externalHighlightedPlaceId,
  googlePlaceResult,
  onClearGooglePlaceResult,
  places,
  onMapLoad,
}) => {
  const { isLoggedIn: canCreatePlace, user: firebaseUser } = useAppAuthState();
  const { user } = useUser(firebaseUser?.uid);
  const { AlertModal, openModal: openAlertModal } = useAlertModal();

  const ref = useRef<HTMLDivElement>(null);
  const [map, setMap] = useState<google.maps.Map>();
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [highlightedPlaceId, setHighlightedPlaceId] = useState<string | null>(
    externalHighlightedPlaceId ?? null
  );

  const markersRef = useRef<
    Map<string, google.maps.marker.AdvancedMarkerElement>
  >(new Map());

  const googlePlaceResultMarkerRef =
    useRef<google.maps.marker.AdvancedMarkerElement | null>(null);
  const googlePlaceResultMarkerIdPlaceIdRef = useRef<string | null>(null);

  const infoWindowsRef = useRef<Map<string, google.maps.InfoWindow>>(new Map());
  const currentInfoWindowRef = useRef<google.maps.InfoWindow | null>(null);

  const existingPlaceIds = useMemo(() => {
    return new Set(places.map((place) => place.googlePlaceId));
  }, [places]);

  const handleCreatePlace = useCallback(
    async (_infoWindow: google.maps.InfoWindow) => {
      if (canCreatePlace) {
        if (googlePlaceResult && user) {
          await createPlaceFromGooglePlaceResultIfDNE(user, googlePlaceResult);
          // This is lazy because I couldn't figure out how get the map to the correct state.
          window.location.href = `/map#${googlePlaceResult.place_id}`;
          window.location.reload();
        }
      } else {
        openAlertModal(
          "Login Required",
          "You'll need to be logged in to list a new place."
        );
      }
    },
    [user, canCreatePlace, googlePlaceResult, openAlertModal]
  );

  const handleClearGooglePlaceResult = useCallback(() => {
    setHighlightedPlaceId(null);
    if (googlePlaceResultMarkerRef.current) {
      removeMarkerFromMap(googlePlaceResultMarkerRef.current);
      googlePlaceResultMarkerRef.current = null;
      googlePlaceResultMarkerIdPlaceIdRef.current = null;
    }
    onClearGooglePlaceResult();
  }, [onClearGooglePlaceResult]);

  const handleInfoWindowClick = useCallback(
    (
      infoWindow: google.maps.InfoWindow,
      marker: google.maps.marker.AdvancedMarkerElement
    ) => {
      if (currentInfoWindowRef.current) {
        currentInfoWindowRef.current.close();
      }

      infoWindow.open(map, marker);
      currentInfoWindowRef.current = infoWindow;
    },
    [map]
  );

  const handlePhotoClick = useCallback(
    (index: number, _photo: google.maps.places.PlacePhoto) => {
      setLightboxIndex(index);
      setLightboxOpen(true);
    },
    []
  );

  useEffect(() => {
    if (highlightedPlaceId && currentInfoWindowRef.current) {
      currentInfoWindowRef.current.close();
    }
  }, [highlightedPlaceId]);

  useEffect(() => {
    if (userLocation && map) {
      map.setCenter(userLocation);
      map.setZoom(15);
    }
  }, [userLocation, map]);

  useEffect(() => {
    if (!map) {
      return;
    }

    if (googlePlaceResultMarkerRef.current) {
      // If the marker is the same as the current googlePlaceResult, do nothing
      if (
        googlePlaceResult?.place_id ===
        googlePlaceResultMarkerIdPlaceIdRef.current
      ) {
        return;
      }

      if (!googlePlaceResult) {
        handleClearGooglePlaceResult();
        return;
      }
    }

    if (googlePlaceResult) {
      if (
        !googlePlaceResult.place_id ||
        existingPlaceIds.has(googlePlaceResult.place_id)
      ) {
        return;
      }

      const { marker: newMarker, infoWindow: newInfoWindow } =
        renderGooglePlacesMarker(
          map,
          googlePlaceResult,
          handleInfoWindowClick,
          handleClearGooglePlaceResult,
          handleCreatePlace,
          handlePhotoClick
        );

      // Open the info window for the new marker
      window.setTimeout(() => {
        handleInfoWindowClick(newInfoWindow, newMarker);
      }, 500);

      googlePlaceResultMarkerRef.current = newMarker;
      googlePlaceResultMarkerIdPlaceIdRef.current = googlePlaceResult.place_id;
    }
  }, [
    existingPlaceIds,
    googlePlaceResult,
    handleCreatePlace,
    handleInfoWindowClick,
    handleClearGooglePlaceResult,
    handlePhotoClick,
    map,
  ]);

  const clearAllMarkers = useCallback(() => {
    markersRef.current.forEach((marker) => (marker.map = null));
    markersRef.current.clear();
    infoWindowsRef.current.clear();
  }, []);

  const handleInfoWindowClose = useCallback(() => {
    if (currentInfoWindowRef.current) {
      currentInfoWindowRef.current.close();
      currentInfoWindowRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (map) {
      map.setCenter(center);
      map.setZoom(zoom);
    }
  }, [map, center, zoom]);

  useEffect(() => {
    if (ref.current && !map) {
      const newMap = new window.google.maps.Map(ref.current, {
        center,
        zoom,
        disableDefaultUI: true,
        mapId: config.googleMapsMapId,
        gestureHandling: "greedy",
      });
      setMap(newMap);
      onMapLoad(newMap);

      newMap.addListener("click", handleInfoWindowClose);
    }
  }, [ref, map, center, zoom, onMapLoad, handleInfoWindowClose]);

  useEffect(() => {
    if (!map) {
      return;
    }

    // Reset all markers to normal state first
    markersRef.current.forEach((marker) => {
      if (marker.element) {
        clearMarkerHighlight(marker);
      }
    });

    if (highlightedPlaceId) {
      const marker = markersRef.current.get(highlightedPlaceId);
      if (marker && marker.element) {
        highlightMarker(marker);
      }
    }

    // Get current place IDs and new place IDs
    const currentPlaceIds = new Set(markersRef.current.keys());
    const newPlaceIds = new Set(places.map((place) => place.googlePlaceId));

    // Remove markers that are no longer in the places array
    Array.from(currentPlaceIds).forEach((placeId) => {
      if (!newPlaceIds.has(placeId)) {
        const marker = markersRef.current.get(placeId);
        if (marker) {
          marker.map = null;
        }
        markersRef.current.delete(placeId);
        infoWindowsRef.current.delete(placeId);
      }
    });

    // Add markers for new places
    places.forEach((place) => {
      if (!currentPlaceIds.has(place.googlePlaceId)) {
        const newMarker = new window.google.maps.marker.AdvancedMarkerElement({
          position: { lat: place.lat, lng: place.lng },
          map: map,
          title: place.name,
        });

        const tableString = summarizeTableSituation(place.numberOfTables);
        const vibeString =
          place.vibes && place.vibes.length > 0
            ? `<div class="metadata-item">Vibes: ${place.vibes.map(vibeTypeToText).join(", ")}</div>`
            : "";

        const newInfoWindow = new window.google.maps.InfoWindow({
          headerDisabled: true,
          content: `
            <div class="info-window-place">
              <div class="name">${place.name}</div>
              <div class="metadata">
                <div class="metadata-item">
                  Tables: <span>${tableString}</span>
                </div>
                ${vibeString}
                <div class="actions">
                  <a class="btn btn-outline-primary" href="#${place.googlePlaceId}">View</a>
                </div>
              </div>
            </div>`,
        });

        newMarker.addListener(
          "gmp-click",
          (event: google.maps.MapMouseEvent) => {
            event.stop?.();
            handleInfoWindowClick(newInfoWindow, newMarker);
          }
        );

        if (place.googlePlaceId === highlightedPlaceId) {
          highlightMarker(newMarker);
        }

        markersRef.current.set(place.googlePlaceId, newMarker);
        infoWindowsRef.current.set(place.googlePlaceId, newInfoWindow);
      }
    });
  }, [map, places, handleInfoWindowClick, highlightedPlaceId]);

  // Clean up on unmount
  useEffect(() => {
    return () => {
      clearAllMarkers();
    };
  }, [clearAllMarkers]);

  return (
    <>
      <div ref={ref} style={{ width: "100%", height: "100%" }} />
      <UserLocationMarker map={map} position={userLocation} />
      <AlertModal />
      {lightboxOpen && googlePlaceResult?.photos && (
        <Lightbox
          open={lightboxOpen}
          close={() => setLightboxOpen(false)}
          slides={googlePlaceResult.photos.map((photo) => ({
            src: photo.getUrl(),
          }))}
          index={lightboxIndex}
        />
      )}
    </>
  );
};

export default MapComponent;
